import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { FaFilter, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { MdDownload, MdSearch } from 'react-icons/md';
import {
  asignarPanelGestionador,
  guardarPanelComentario,
  guardarPanelPendientes,
} from '../api/apiConfig';
import { canAssignPanelGestionador } from '../utils/gestionCampanaAccess';
import {
  labelPanelPendientes,
  normalizePanelPendientes,
  PANEL_PENDIENTES_OPTS,
  prioridadBadgeClass,
  prioridadConfig,
} from '../utils/panelOperativo';
import { applyColumnFilters, filterData, sortData } from '../utils/tableSortSearch';
import '../pages/PanelComercial.css';

const SORT_OPS = { dateColumns: ['updatedAt', 'asignadoEn'] };
const GESTIONADORES = ['Ivett', 'Lissette', 'Javiera'];
export const OPS_COL_LABELS = {
  rut: 'RUT',
  nOperacion: 'N° OPERACIÓN',
  td: 'TD',
  cliente: 'CLIENTE',
  ejecutivo: 'EJECUTIVO',
  area: 'ÁREA',
  gestionador: 'GESTIONADOR',
  asignadoEn: 'ASIGNADO',
  priority: 'PRIORIDAD',
  days: 'DÍAS EN ESPERA (HÁBILES)',
  updatedAt: 'ÚLTIMA ACTUALIZACIÓN',
  pendientes: 'PENDIENTES',
  comentario: 'COMENTARIO',
};
const COMMENT_MAX = 500;
const COL_WIDTH_MIN = 72;
const COL_WIDTH_MAX = 520;
const DEFAULT_COL_WIDTHS = Object.freeze({
  rut: 110,
  nOperacion: 108,
  td: 72,
  cliente: 180,
  ejecutivo: 140,
  area: 140,
  gestionador: 120,
  asignadoEn: 120,
  priority: 100,
  days: 120,
  updatedAt: 140,
  pendientes: 160,
  comentario: 180,
});

function readColWidths(storageKey) {
  if (!storageKey || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = Math.min(COL_WIDTH_MAX, Math.max(COL_WIDTH_MIN, Math.round(n)));
    }
    return out;
  } catch {
    return {};
  }
}

function writeColWidths(storageKey, widths) {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    /* ignore quota / private mode */
  }
}

const opsSearchFields = (o) => [
  o.rut,
  o.nOperacion,
  o.td,
  o.cliente,
  o.ejecutivo,
  o.area || o.stage,
  o.priority,
  o.days,
  o.updatedAt,
  o.gestionador,
  o.asignadoEn,
  labelPanelPendientes(o.pendientes),
  o.comentario,
];

export function useTableSortSearch(rows, fieldsGetter, sortOptions, extra = {}) {
  const { columnFilters, getFilterLabel } = extra;
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const searchRows = useMemo(
    () => filterData(rows || [], search, fieldsGetter),
    [rows, search, fieldsGetter],
  );
  const columnFiltered = useMemo(() => {
    if (!getFilterLabel) return searchRows;
    return applyColumnFilters(searchRows, columnFilters, getFilterLabel);
  }, [searchRows, columnFilters, getFilterLabel]);
  const sorted = useMemo(
    () => sortData(columnFiltered, sortColumn, sortDirection, sortOptions),
    [columnFiltered, sortColumn, sortDirection, sortOptions],
  );

  const toggleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const reset = useCallback(() => {
    setSearch('');
    setSortColumn(null);
    setSortDirection('asc');
  }, []);

  return {
    search,
    setSearch,
    sortColumn,
    sortDirection,
    toggleSort,
    rows: sorted,
    searchRows,
    total: (rows || []).length,
    filteredTotal: columnFiltered.length,
    reset,
  };
}

export function SortTh({
  column,
  label,
  sortColumn,
  sortDirection,
  onSort,
  onFilter,
  filterActive,
  filterOpen,
  width,
  onResizeStart,
}) {
  return (
    <th
      className="pc-th-sort"
      style={
        width
          ? { width, minWidth: width, maxWidth: width }
          : undefined
      }
    >
      <div className="pc-th-inner">
        <button type="button" className="pc-th-sort-btn" onClick={() => onSort(column)}>
          <span>{label}</span>
          {sortColumn !== column ? (
            <FaSort className="pc-sort-icon idle" />
          ) : sortDirection === 'asc' ? (
            <FaSortUp className="pc-sort-icon" />
          ) : (
            <FaSortDown className="pc-sort-icon" />
          )}
        </button>
        {onFilter ? (
          <button
            type="button"
            className={`pc-th-filter-btn${filterActive ? ' active' : ''}${filterOpen ? ' open' : ''}`}
            title="Filtrar valores"
            aria-label={`Filtrar ${label}`}
            aria-pressed={filterActive ? 'true' : 'false'}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onFilter(column, e.currentTarget);
            }}
          >
            <FaFilter />
          </button>
        ) : null}
      </div>
      {onResizeStart ? (
        <span
          className="pc-th-resize"
          role="separator"
          aria-orientation="vertical"
          aria-label={`Redimensionar ${label}`}
          title="Arrastrar para ajustar ancho"
          onMouseDown={(e) => onResizeStart(column, e)}
        />
      ) : null}
    </th>
  );
}

function ExcelFilterMenu({
  open,
  pos,
  title,
  menuRef,
  search,
  onSearch,
  visibleOptions,
  checked,
  setChecked,
  hasActiveFilter,
  onClear,
  onCancel,
  onApply,
}) {
  if (!open || !pos) return null;
  const allVisibleOn =
    visibleOptions.length > 0 && visibleOptions.every((u) => checked[u]);
  const setVisible = (on) => {
    setChecked((prev) => {
      const next = { ...prev };
      for (const u of visibleOptions) next[u] = on;
      return next;
    });
  };
  return createPortal(
    <div
      ref={menuRef}
      className="pc-excel-filter"
      style={{ top: pos.top, left: pos.left }}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-label={`Filtro ${title}`}
    >
      <div className="pc-excel-filter-title">Filtrar: {title}</div>
      <div className="pc-excel-filter-toolbar">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Buscar valor…"
          autoFocus
        />
        <div className="pc-excel-filter-tools">
          <button type="button" onClick={() => setVisible(true)}>
            Marcar visibles
          </button>
          <button type="button" onClick={() => setVisible(false)}>
            Desmarcar visibles
          </button>
          {hasActiveFilter ? (
            <button type="button" className="link" onClick={onClear}>
              Borrar filtro
            </button>
          ) : null}
        </div>
      </div>
      <div className="pc-excel-filter-list">
        <label className="pc-excel-filter-item sticky">
          <input
            type="checkbox"
            checked={allVisibleOn}
            onChange={(e) => setVisible(e.target.checked)}
          />
          <span>(Seleccionar todo lo visible)</span>
        </label>
        {visibleOptions.length === 0 ? (
          <div className="pc-excel-filter-empty">Sin coincidencias</div>
        ) : (
          visibleOptions.map((u) => (
            <label key={u} className="pc-excel-filter-item" title={u}>
              <input
                type="checkbox"
                checked={!!checked[u]}
                onChange={(e) =>
                  setChecked((prev) => ({ ...prev, [u]: e.target.checked }))
                }
              />
              <span>{u}</span>
            </label>
          ))
        )}
      </div>
      <div className="pc-excel-filter-foot">
        <button type="button" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="pc-excel-filter-apply" onClick={onApply}>
          Aplicar
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function TableSearch({ value, onChange, placeholder }) {
  return (
    <div className="pc-search-box">
      <input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <MdSearch />
    </div>
  );
}

function placeCommentPop(anchor, width = 400, estHeight = 280) {
  const r = anchor.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = Math.min(Math.max(8, r.right - width), Math.max(8, vw - width - 8));
  let top = r.bottom + 8;
  if (top + estHeight > vh - 8) {
    top = Math.max(8, r.top - estHeight - 8);
  }
  return { top, left };
}

function CommentCell({
  nOperacion,
  value,
  draft,
  onDraftChange,
  onSave,
  canEdit,
  saving,
  open,
  onOpen,
  onClose,
}) {
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  const display = draft !== undefined ? draft : value || '';
  const hasText = Boolean(String(value || '').trim());

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setPos(null);
      return undefined;
    }
    const update = () => setPos(placeCommentPop(triggerRef.current));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      if (canEdit) onSave(nOperacion, display);
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDraftChange(nOperacion, null);
        onClose();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, canEdit, display, nOperacion, onSave, onClose, onDraftChange]);

  const preview = hasText ? String(value) : canEdit ? 'Agregar comentario…' : '—';

  return (
    <td className="pc-comment-cell">
      <button
        type="button"
        ref={triggerRef}
        className={`pc-comment-preview${hasText ? '' : ' empty'}${open ? ' open' : ''}`}
        title={hasText ? String(value) : canEdit ? 'Escribir comentario' : ''}
        disabled={saving}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {preview}
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className="pc-comment-pop"
              style={{ top: pos.top, left: pos.left }}
              role="dialog"
              aria-label="Comentario"
            >
              <div className="pc-comment-pop-title">Comentario</div>
              {canEdit ? (
                <>
                  <textarea
                    className="pc-comment-textarea"
                    maxLength={COMMENT_MAX}
                    value={display}
                    disabled={saving}
                    autoFocus
                    placeholder="Escribe un comentario…"
                    onChange={(e) => onDraftChange(nOperacion, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        onSave(nOperacion, display);
                        onClose();
                      }
                    }}
                  />
                  <div className="pc-comment-pop-foot">
                    <span className="pc-comment-count">
                      {String(display).length}/{COMMENT_MAX}
                    </span>
                    <button
                      type="button"
                      className="pc-btn small"
                      onClick={() => {
                        onDraftChange(nOperacion, null);
                        onClose();
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="pc-btn small primary"
                      disabled={saving}
                      onClick={() => {
                        onSave(nOperacion, display);
                        onClose();
                      }}
                    >
                      Guardar
                    </button>
                  </div>
                </>
              ) : (
                <div className="pc-comment-readonly">{hasText ? String(value) : 'Sin comentario'}</div>
              )}
            </div>,
            document.body,
          )
        : null}
    </td>
  );
}

function PendientesCell({
  nOperacion,
  value,
  draft,
  onDraftChange,
  onSave,
  canEdit,
  saving,
  open,
  onOpen,
  onClose,
}) {
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  const selected = normalizePanelPendientes(draft !== undefined ? draft : value);
  const hasItems = selected.length > 0;
  const preview = hasItems
    ? labelPanelPendientes(selected)
    : canEdit
      ? 'Seleccionar…'
      : '—';

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setPos(null);
      return undefined;
    }
    const update = () => setPos(placeCommentPop(triggerRef.current, 280, 260));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      if (canEdit) onSave(nOperacion, selected);
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDraftChange(nOperacion, null);
        onClose();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, canEdit, selected, nOperacion, onSave, onClose, onDraftChange]);

  const toggle = (id) => {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onDraftChange(nOperacion, PANEL_PENDIENTES_OPTS.map((o) => o.id).filter((x) => set.has(x)));
  };

  return (
    <td className="pc-pendientes-cell">
      <button
        type="button"
        ref={triggerRef}
        className={`pc-pendientes-preview${hasItems ? '' : ' empty'}${open ? ' open' : ''}`}
        title={hasItems ? preview : canEdit ? 'Marcar pendientes' : ''}
        disabled={saving}
        onClick={() => (open ? onClose() : onOpen())}
      >
        {preview}
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              className="pc-pendientes-pop"
              style={{ top: pos.top, left: pos.left }}
              role="dialog"
              aria-label="Pendientes"
            >
              <div className="pc-comment-pop-title">Pendientes</div>
              {canEdit ? (
                <>
                  <div className="pc-pendientes-list">
                    {PANEL_PENDIENTES_OPTS.map((opt) => (
                      <label key={opt.id} className="pc-pendientes-option">
                        <input
                          type="checkbox"
                          checked={selected.includes(opt.id)}
                          disabled={saving}
                          onChange={() => toggle(opt.id)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="pc-comment-pop-foot">
                    <button
                      type="button"
                      className="pc-btn small"
                      onClick={() => {
                        onDraftChange(nOperacion, null);
                        onClose();
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="pc-btn small primary"
                      disabled={saving}
                      onClick={() => {
                        onSave(nOperacion, selected);
                        onClose();
                      }}
                    >
                      Guardar
                    </button>
                  </div>
                </>
              ) : (
                <div className="pc-comment-readonly">
                  {hasItems ? labelPanelPendientes(selected) : 'Sin pendientes marcados'}
                </div>
              )}
            </div>,
            document.body,
          )
        : null}
    </td>
  );
}

export function fmtAsignado(value) {
  if (!value) return '';
  const s = String(value).trim().replace('T', ' ');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;
  return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}`;
}

function downloadXlsx(fileName, sheets) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
  });
  XLSX.writeFile(wb, fileName);
}

function mapOpsExportRow(o) {
  return {
    RUT: o.rut || '',
    CLIENTE: o.cliente || '',
    EJECUTIVO: o.ejecutivo || '',
    AREA: o.area || o.stage || '',
    N_OPERACION: o.nOperacion ?? '',
    TD: o.td || '',
    GESTIONADOR: o.gestionador || '',
    ASIGNADO_EN: o.asignadoEn || '',
    PRIORIDAD: o.priority || '',
    DIAS_EN_ESPERA: o.days ?? '',
    FECHA_CREACION_OPERACION: o.createdAt || '',
    ULTIMA_ACTUALIZACION: o.updatedAt || '',
    PENDIENTES: labelPanelPendientes(o.pendientes),
    COMENTARIO: o.comentario || '',
  };
}

function opsCellLabel(o, key) {
  const empty = (v) => {
    if (v === null || v === undefined) return '(Vacío)';
    const s = String(v).trim();
    return s || '(Vacío)';
  };
  switch (key) {
    case 'area':
      return empty(o.area || o.stage);
    case 'gestionador':
      return String(o.gestionador || '').trim() || 'Sin asignar';
    case 'asignadoEn':
      return fmtAsignado(o.asignadoEn) || '(Vacío)';
    case 'days':
      return o.days === null || o.days === undefined || o.days === '' ? '(Vacío)' : String(o.days);
    case 'pendientes': {
      const label = labelPanelPendientes(o.pendientes);
      return label || '(Vacío)';
    }
    default:
      return empty(o[key]);
  }
}

function BlockError({ message }) {
  if (!message) return null;
  return <div className="pc-block-error">{message}</div>;
}

export default function PanelSeguimientoOperaciones({
  operations,
  loading,
  error,
  fecha,
  user,
  exportFileName,
  extraActions,
  onPatch,
  prioridad,
  /** Columnas a ocultar (ej. gestión en Panel Gerencial). */
  hideColumns = [],
  /** Sobrescribe etiquetas de columnas visibles. */
  columnLabels = null,
  /** Si false, pendientes/comentario solo lectura (sin editar ni asignar). */
  allowEditGestion = true,
  /** Clave localStorage para persistir anchos de columna. */
  columnWidthsKey = 'mgc.panelOps.colWidths.v1',
}) {
  const canAssign = allowEditGestion && canAssignPanelGestionador(user);
  const prio = prioridad || prioridadConfig();
  const hiddenCols = useMemo(() => new Set(hideColumns || []), [hideColumns]);
  const visibleColEntries = useMemo(() => {
    const labels = { ...OPS_COL_LABELS, ...(columnLabels || {}) };
    return Object.entries(labels).filter(([key]) => !hiddenCols.has(key) && OPS_COL_LABELS[key]);
  }, [hiddenCols, columnLabels]);
  const visibleColCount = visibleColEntries.length;
  const showCol = (key) => !hiddenCols.has(key);
  const [colWidths, setColWidths] = useState(() => ({
    ...DEFAULT_COL_WIDTHS,
    ...readColWidths(columnWidthsKey),
  }));
  const resizeDragRef = useRef(null);
  const [savingOpe, setSavingOpe] = useState(null);
  const [savingComment, setSavingComment] = useState(null);
  const [savingPendientes, setSavingPendientes] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentEditorKey, setCommentEditorKey] = useState(null);
  const [pendientesDrafts, setPendientesDrafts] = useState({});
  const [pendientesEditorKey, setPendientesEditorKey] = useState(null);
  const [opsColumnFilters, setOpsColumnFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterMenuSearch, setFilterMenuSearch] = useState('');
  const [filterMenuChecked, setFilterMenuChecked] = useState({});
  const [filterMenuPos, setFilterMenuPos] = useState(null);
  const filterMenuRef = useRef(null);

  useEffect(() => {
    setColWidths({
      ...DEFAULT_COL_WIDTHS,
      ...readColWidths(columnWidthsKey),
    });
  }, [columnWidthsKey]);

  const colWidthOf = useCallback(
    (key) => colWidths[key] || DEFAULT_COL_WIDTHS[key] || 120,
    [colWidths],
  );

  const tableMinWidth = useMemo(
    () => visibleColEntries.reduce((sum, [key]) => sum + colWidthOf(key), 0),
    [visibleColEntries, colWidthOf],
  );

  const handleColResizeStart = useCallback(
    (column, e) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = colWidthOf(column);
      resizeDragRef.current = { column, startX, startW };
      document.body.classList.add('pc-col-resizing');

      const onMove = (ev) => {
        const drag = resizeDragRef.current;
        if (!drag) return;
        const next = Math.min(
          COL_WIDTH_MAX,
          Math.max(COL_WIDTH_MIN, Math.round(drag.startW + (ev.clientX - drag.startX))),
        );
        setColWidths((prev) => {
          if (prev[drag.column] === next) return prev;
          return { ...prev, [drag.column]: next };
        });
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.classList.remove('pc-col-resizing');
        const drag = resizeDragRef.current;
        resizeDragRef.current = null;
        if (!drag) return;
        setColWidths((prev) => {
          writeColWidths(columnWidthsKey, prev);
          return prev;
        });
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [colWidthOf, columnWidthsKey],
  );

  const opsTable = useTableSortSearch(operations, opsSearchFields, SORT_OPS, {
    columnFilters: opsColumnFilters,
    getFilterLabel: opsCellLabel,
  });

  const uniquesForOpenColumn = useMemo(() => {
    if (!openFilterKey) return [];
    const base = applyColumnFilters(
      opsTable.searchRows,
      opsColumnFilters,
      opsCellLabel,
      openFilterKey,
    );
    const uniq = new Set();
    for (const r of base) uniq.add(opsCellLabel(r, openFilterKey));
    return Array.from(uniq).sort((a, b) =>
      a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }),
    );
  }, [openFilterKey, opsTable.searchRows, opsColumnFilters]);

  const visibleFilterOptions = useMemo(() => {
    const q = filterMenuSearch.trim().toLowerCase();
    if (!q) return uniquesForOpenColumn;
    return uniquesForOpenColumn.filter((u) => u.toLowerCase().includes(q));
  }, [uniquesForOpenColumn, filterMenuSearch]);

  const activeColumnFilterCount = useMemo(
    () => Object.values(opsColumnFilters).filter((a) => Array.isArray(a) && a.length > 0).length,
    [opsColumnFilters],
  );

  useEffect(() => {
    setOpsColumnFilters({});
    setOpenFilterKey(null);
    setFilterMenuPos(null);
    opsTable.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  useEffect(() => {
    if (!openFilterKey) return undefined;
    const onDoc = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setOpenFilterKey(null);
        setFilterMenuPos(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openFilterKey]);

  const closeFilterMenu = () => {
    setOpenFilterKey(null);
    setFilterMenuPos(null);
  };

  const openColumnFilter = (colKey, anchorEl) => {
    if (openFilterKey === colKey) {
      closeFilterMenu();
      return;
    }
    const r = anchorEl.getBoundingClientRect();
    const panelW = 280;
    const maxH = Math.min(420, window.innerHeight - 16);
    let top = r.bottom + 4;
    if (top + maxH > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - maxH - 8);
    }
    setFilterMenuPos({
      top,
      left: Math.max(8, Math.min(r.left, window.innerWidth - panelW - 8)),
    });
    const base = applyColumnFilters(opsTable.searchRows, opsColumnFilters, opsCellLabel, colKey);
    const uniq = new Set();
    for (const row of base) uniq.add(opsCellLabel(row, colKey));
    const sortedUniq = Array.from(uniq).sort((a, b) =>
      a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }),
    );
    const allowed = opsColumnFilters[colKey];
    const checked = {};
    for (const u of sortedUniq) {
      checked[u] = !allowed || allowed.length === 0 || allowed.includes(u);
    }
    setFilterMenuSearch('');
    setFilterMenuChecked(checked);
    setCommentEditorKey(null);
    setOpenFilterKey(colKey);
  };

  const applyColumnFilter = () => {
    if (!openFilterKey) return;
    const selected = Object.entries(filterMenuChecked)
      .filter(([, on]) => on)
      .map(([label]) => label);
    const selectedSet = new Set(selected);
    const chosen = uniquesForOpenColumn.filter((u) => selectedSet.has(u));
    if (chosen.length === 0) {
      window.alert('Selecciona al menos un valor.');
      return;
    }
    setOpsColumnFilters((prev) => {
      const next = { ...prev };
      if (chosen.length >= uniquesForOpenColumn.length) {
        delete next[openFilterKey];
      } else {
        next[openFilterKey] = chosen;
      }
      return next;
    });
    closeFilterMenu();
  };

  const clearColumnFilter = (colKey) => {
    setOpsColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
    if (openFilterKey === colKey) closeFilterMenu();
  };

  const clearAllColumnFilters = () => {
    setOpsColumnFilters({});
    closeFilterMenu();
  };

  const handleAssign = async (nOperacion, gestionador) => {
    if (!canAssign || nOperacion == null || nOperacion === '') return;
    const key = String(nOperacion);
    setSavingOpe(key);
    try {
      const result = await asignarPanelGestionador(user, {
        nOperacion,
        gestionador,
        fecha,
      });
      if (result && result.ok !== false) {
        onPatch?.(nOperacion, {
          gestionador: result.gestionador || '',
          asignadoEn: result.asignadoEn || '',
          ...(result.comentario !== undefined ? { comentario: result.comentario || '' } : {}),
          ...(result.pendientes !== undefined
            ? { pendientes: normalizePanelPendientes(result.pendientes) }
            : {}),
        });
      }
    } catch (err) {
      console.error('Error al asignar gestionador:', err);
    } finally {
      setSavingOpe(null);
    }
  };

  const handleCommentSave = async (nOperacion, rawValue) => {
    if (!canAssign || nOperacion == null || nOperacion === '') return;
    const key = String(nOperacion);
    const next = String(rawValue ?? '').trim().slice(0, COMMENT_MAX);
    const current = String(
      operations.find((o) => String(o.nOperacion ?? '') === key)?.comentario || '',
    ).trim();
    if (next === current) {
      setCommentDrafts((prev) => {
        if (!(key in prev)) return prev;
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      return;
    }
    setSavingComment(key);
    try {
      const result = await guardarPanelComentario(user, {
        nOperacion,
        comentario: next,
        fecha,
      });
      if (result && result.ok !== false) {
        onPatch?.(nOperacion, {
          comentario: result.comentario || '',
          ...(result.gestionador !== undefined ? { gestionador: result.gestionador || '' } : {}),
          ...(result.asignadoEn !== undefined ? { asignadoEn: result.asignadoEn || '' } : {}),
          ...(result.pendientes !== undefined
            ? { pendientes: normalizePanelPendientes(result.pendientes) }
            : {}),
        });
        setCommentDrafts((prev) => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      }
    } catch (err) {
      console.error('Error al guardar comentario:', err);
    } finally {
      setSavingComment(null);
    }
  };

  const samePendientes = (a, b) => {
    const left = normalizePanelPendientes(a).join('|');
    const right = normalizePanelPendientes(b).join('|');
    return left === right;
  };

  const handlePendientesSave = async (nOperacion, rawValue) => {
    if (!canAssign || nOperacion == null || nOperacion === '') return;
    const key = String(nOperacion);
    const next = normalizePanelPendientes(rawValue);
    const current = normalizePanelPendientes(
      operations.find((o) => String(o.nOperacion ?? '') === key)?.pendientes,
    );
    if (samePendientes(next, current)) {
      setPendientesDrafts((prev) => {
        if (!(key in prev)) return prev;
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      return;
    }
    setSavingPendientes(key);
    try {
      const result = await guardarPanelPendientes(user, {
        nOperacion,
        pendientes: next,
        fecha,
      });
      if (result && result.ok !== false) {
        onPatch?.(nOperacion, {
          pendientes: normalizePanelPendientes(result.pendientes),
          ...(result.comentario !== undefined ? { comentario: result.comentario || '' } : {}),
          ...(result.gestionador !== undefined ? { gestionador: result.gestionador || '' } : {}),
          ...(result.asignadoEn !== undefined ? { asignadoEn: result.asignadoEn || '' } : {}),
        });
        setPendientesDrafts((prev) => {
          const copy = { ...prev };
          delete copy[key];
          return copy;
        });
      }
    } catch (err) {
      console.error('Error al guardar pendientes:', err);
    } finally {
      setSavingPendientes(null);
    }
  };

  const handlePendientesDraft = useCallback((nOperacion, val) => {
    const key = String(nOperacion);
    setPendientesDrafts((prev) => {
      if (val === null) {
        if (!(key in prev)) return prev;
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: normalizePanelPendientes(val) };
    });
  }, []);

  const handleCommentDraft = useCallback((nOperacion, val) => {
    const key = String(nOperacion);
    setCommentDrafts((prev) => {
      if (val === null) {
        if (!(key in prev)) return prev;
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: val };
    });
  }, []);

  const exportExcel = () => {
    const rows = opsTable.rows.map((o) => {
      const full = mapOpsExportRow(o);
      if (!hiddenCols.size) return full;
      const out = { ...full };
      if (hiddenCols.has('gestionador')) delete out.GESTIONADOR;
      if (hiddenCols.has('asignadoEn')) delete out.ASIGNADO_EN;
      if (hiddenCols.has('pendientes')) delete out.PENDIENTES;
      if (hiddenCols.has('comentario')) delete out.COMENTARIO;
      return out;
    });
    downloadXlsx(exportFileName || `panel_operaciones_${fecha}.xlsx`, [
      { name: 'Seguimiento', rows },
    ]);
  };

  return (
    <>
      <section className="pc-card pc-table-card">
        <div className="pc-table-head">
          <div>
            <div className="pc-card-title">SEGUIMIENTO DE OPERACIONES PENDIENTES</div>
            {(opsTable.search || activeColumnFilterCount > 0) && opsTable.total > 0 ? (
              <div className="pc-table-count">
                {opsTable.filteredTotal} de {opsTable.total} operaciones
              </div>
            ) : null}
          </div>
          <div className="pc-table-actions">
            {extraActions}
            <TableSearch
              value={opsTable.search}
              onChange={opsTable.setSearch}
              placeholder="Buscar cliente, RUT, n° operación, TD, ejecutivo o área..."
            />
            {activeColumnFilterCount > 0 ? (
              <button type="button" className="pc-btn small" onClick={clearAllColumnFilters}>
                Quitar filtros ({activeColumnFilterCount})
              </button>
            ) : null}
            <button type="button" className="pc-btn small" onClick={exportExcel}>
              <MdDownload />
              EXPORTAR
            </button>
          </div>
        </div>
        <BlockError message={error} />
        <div className="pc-table-wrap">
          <table className="pc-main-table pc-main-table-resizable" style={{ minWidth: tableMinWidth }}>
            <colgroup>
              {visibleColEntries.map(([column]) => (
                <col key={column} style={{ width: colWidthOf(column) }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {visibleColEntries.map(([column, label]) => (
                  <SortTh
                    key={column}
                    column={column}
                    label={label}
                    sortColumn={opsTable.sortColumn}
                    sortDirection={opsTable.sortDirection}
                    onSort={opsTable.toggleSort}
                    onFilter={openColumnFilter}
                    filterActive={Array.isArray(opsColumnFilters[column]) && opsColumnFilters[column].length > 0}
                    filterOpen={openFilterKey === column}
                    width={colWidthOf(column)}
                    onResizeStart={handleColResizeStart}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {opsTable.rows.length === 0 ? (
                <tr>
                  <td colSpan={visibleColCount} className="pc-empty-state">
                    {loading
                      ? 'Cargando…'
                      : opsTable.total === 0
                        ? 'No hay resultados.'
                        : 'Ninguna fila coincide con la búsqueda o los filtros.'}
                  </td>
                </tr>
              ) : (
                opsTable.rows.map((o, idx) => (
                  <tr key={o.id ?? `${o.nOperacion || 'op'}-${o.createdAt}-${idx}`}>
                    {showCol('rut') ? <td>{o.rut || '—'}</td> : null}
                    {showCol('nOperacion') ? <td>{o.nOperacion || '—'}</td> : null}
                    {showCol('td') ? (
                      <td>{o.td ? <span className="pc-td-badge">{o.td}</span> : '—'}</td>
                    ) : null}
                    {showCol('cliente') ? <td>{o.cliente}</td> : null}
                    {showCol('ejecutivo') ? <td>{o.ejecutivo}</td> : null}
                    {showCol('area') ? <td>{o.area || o.stage || '—'}</td> : null}
                    {showCol('gestionador') ? (
                      <td>
                        {canAssign && o.nOperacion != null && o.nOperacion !== '' ? (
                          <select
                            className="pc-gestionador-select"
                            value={o.gestionador || ''}
                            disabled={savingOpe === String(o.nOperacion)}
                            onChange={(e) => handleAssign(o.nOperacion, e.target.value)}
                          >
                            <option value="">Sin asignar</option>
                            {GESTIONADORES.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          o.gestionador || '—'
                        )}
                      </td>
                    ) : null}
                    {showCol('asignadoEn') ? <td>{fmtAsignado(o.asignadoEn) || '—'}</td> : null}
                    {showCol('priority') ? (
                      <td>
                        <span className={`pc-priority-badge pc-priority-${prioridadBadgeClass(o.priority)}`}>
                          {o.priority}
                        </span>
                      </td>
                    ) : null}
                    {showCol('days') ? (
                      <td className={`pc-wait-${prioridadBadgeClass(o.priority)}`}>{o.days}</td>
                    ) : null}
                    {showCol('updatedAt') ? <td>{o.updatedAt}</td> : null}
                    {showCol('pendientes') ? (
                      <PendientesCell
                        nOperacion={o.nOperacion}
                        value={normalizePanelPendientes(o.pendientes)}
                        draft={pendientesDrafts[String(o.nOperacion)]}
                        onDraftChange={handlePendientesDraft}
                        onSave={handlePendientesSave}
                        canEdit={canAssign && o.nOperacion != null && o.nOperacion !== ''}
                        saving={savingPendientes === String(o.nOperacion)}
                        open={pendientesEditorKey === String(o.nOperacion)}
                        onOpen={() => {
                          const key = String(o.nOperacion);
                          setOpenFilterKey(null);
                          setCommentEditorKey(null);
                          setPendientesDrafts((prev) =>
                            key in prev
                              ? prev
                              : { ...prev, [key]: normalizePanelPendientes(o.pendientes) },
                          );
                          setPendientesEditorKey(key);
                        }}
                        onClose={() =>
                          setPendientesEditorKey((cur) => (cur === String(o.nOperacion) ? null : cur))
                        }
                      />
                    ) : null}
                    {showCol('comentario') ? (
                      <CommentCell
                        nOperacion={o.nOperacion}
                        value={o.comentario || ''}
                        draft={commentDrafts[String(o.nOperacion)]}
                        onDraftChange={handleCommentDraft}
                        onSave={handleCommentSave}
                        canEdit={canAssign && o.nOperacion != null && o.nOperacion !== ''}
                        saving={savingComment === String(o.nOperacion)}
                        open={commentEditorKey === String(o.nOperacion)}
                        onOpen={() => {
                          const key = String(o.nOperacion);
                          setOpenFilterKey(null);
                          setPendientesEditorKey(null);
                          setCommentDrafts((prev) =>
                            key in prev ? prev : { ...prev, [key]: o.comentario || '' },
                          );
                          setCommentEditorKey(key);
                        }}
                        onClose={() =>
                          setCommentEditorKey((cur) => (cur === String(o.nOperacion) ? null : cur))
                        }
                      />
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ExcelFilterMenu
          open={Boolean(openFilterKey)}
          pos={filterMenuPos}
          title={
            openFilterKey
              ? (columnLabels && columnLabels[openFilterKey]) || OPS_COL_LABELS[openFilterKey] || ''
              : ''
          }
          menuRef={filterMenuRef}
          search={filterMenuSearch}
          onSearch={setFilterMenuSearch}
          visibleOptions={visibleFilterOptions}
          checked={filterMenuChecked}
          setChecked={setFilterMenuChecked}
          hasActiveFilter={Array.isArray(opsColumnFilters[openFilterKey]) && opsColumnFilters[openFilterKey].length > 0}
          onClear={() => clearColumnFilter(openFilterKey)}
          onCancel={closeFilterMenu}
          onApply={applyColumnFilter}
        />
      </section>

      <section className="pc-footer-grid">
        <article className="pc-footer-card">
          <h3>CRITERIO DE PRIORIZACIÓN</h3>
          <ul>
            <li>
              <strong>Alto:</strong> {prio.altoDesde} o más días hábiles en espera.
            </li>
            <li>
              <strong>Medio:</strong> desde {prio.medioDesde} hasta {prio.altoDesde - 1} días
              hábiles.
            </li>
            <li>
              <strong>Bajo:</strong> menos de {prio.medioDesde} días hábiles.
            </li>
            <li>A igual prioridad se ordena por fecha de creación de operación (FIFO).</li>
          </ul>
        </article>
        <article className="pc-footer-card">
          <h3>DEFINICIÓN DE DÍAS EN ESPERA</h3>
          <ul>
            <li>
              Días hábiles transcurridos desde la fecha de creación de la operación hasta la
              fecha consultada.
            </li>
            <li>No se consideran sábados, domingos ni festivos.</li>
          </ul>
        </article>
        <article className="pc-footer-card">
          <h3>ÁREA (CEO)</h3>
          <ul>
            <li>La columna Área refleja el estado en la carga de <strong>base_ceo</strong>.</li>
            <li>Tesorería, Depositada y En revisión Final no se consideran pendientes.</li>
          </ul>
        </article>
      </section>
    </>
  );
}
